import { ref, uploadBytesResumable, type UploadTaskSnapshot } from 'firebase/storage';
import { storage } from './firebase';

export interface UploadProgress {
  bytesTransferred: number;
  totalBytes: number;
  percent: number;
  state: 'running' | 'paused' | 'success' | 'error' | 'canceled';
}

/**
 * Upload the audit zip directly to Storage. The path triggers the Cloud
 * Function `onAuditUploaded` which parses & writes Firestore docs.
 */
export function uploadAuditZip(
  orgId: string,
  auditId: string,
  file: File,
  onProgress?: (p: UploadProgress) => void,
  uploaderUid?: string,
  /**
   * Empreinte SHA-256 calculée par le navigateur AVANT l'envoi
   * (`validateAuditZip`). Le serveur recalcule toujours la sienne et ne
   * compare que si celle-ci est fournie (ING-19 / SEC-08).
   */
  sha256?: string,
): { promise: Promise<string>; cancel: () => void } {
  const path = `orgs/${orgId}/audits/${auditId}/upload.zip`;
  const r = ref(storage, path);
  const task = uploadBytesResumable(r, file, {
    contentType: 'application/zip',
    customMetadata: {
      orgId,
      auditId,
      originalName: file.name,
      uploadedBy: uploaderUid ?? '',
      ...(sha256 ? { sha256 } : {}),
    },
  });

  const promise = new Promise<string>((resolve, reject) => {
    task.on(
      'state_changed',
      (snap: UploadTaskSnapshot) => {
        if (!onProgress) return;
        onProgress({
          bytesTransferred: snap.bytesTransferred,
          totalBytes: snap.totalBytes,
          percent: snap.totalBytes ? (snap.bytesTransferred / snap.totalBytes) * 100 : 0,
          state: snap.state as UploadProgress['state'],
        });
      },
      err => reject(err),
      () => resolve(path),
    );
  });

  return { promise, cancel: () => task.cancel() };
}

/**
 * Upload a generic organisation document (scope, report PDF, etc.) to Storage.
 * Path must match `orgs/{orgId}/documents/{docId}/{filename}` — the Cloud
 * Function `createOrgDocument` allocates the docId and path before this upload.
 */
export function uploadOrgDocument(
  orgId: string,
  docId: string,
  file: File,
  onProgress?: (p: UploadProgress) => void,
  uploaderUid?: string,
): { promise: Promise<string>; cancel: () => void } {
  const path = `orgs/${orgId}/documents/${docId}/${file.name}`;
  const r = ref(storage, path);
  const task = uploadBytesResumable(r, file, {
    contentType: file.type || 'application/octet-stream',
    customMetadata: {
      orgId,
      docId,
      originalName: file.name,
      uploadedBy: uploaderUid ?? '',
    },
  });

  const promise = new Promise<string>((resolve, reject) => {
    task.on(
      'state_changed',
      (snap: UploadTaskSnapshot) => {
        if (!onProgress) return;
        onProgress({
          bytesTransferred: snap.bytesTransferred,
          totalBytes: snap.totalBytes,
          percent: snap.totalBytes ? (snap.bytesTransferred / snap.totalBytes) * 100 : 0,
          state: snap.state as UploadProgress['state'],
        });
      },
      err => reject(err),
      () => resolve(path),
    );
  });

  return { promise, cancel: () => task.cancel() };
}

/**
 * Upload a single image attachment for a support ticket message.
 * The `storagePath` is pre-allocated by the createTicket / addTicketMessage Cloud Function.
 */
export function uploadTicketAttachment(
  storagePath: string,
  file: File,
  onProgress?: (p: UploadProgress) => void,
): { promise: Promise<string>; cancel: () => void } {
  const r = ref(storage, storagePath);
  const task = uploadBytesResumable(r, file, {
    contentType: file.type,
    customMetadata: { originalName: file.name },
  });

  const promise = new Promise<string>((resolve, reject) => {
    task.on(
      'state_changed',
      (snap: UploadTaskSnapshot) => {
        if (!onProgress) return;
        onProgress({
          bytesTransferred: snap.bytesTransferred,
          totalBytes: snap.totalBytes,
          percent: snap.totalBytes ? (snap.bytesTransferred / snap.totalBytes) * 100 : 0,
          state: snap.state as UploadProgress['state'],
        });
      },
      err => reject(err),
      () => resolve(storagePath),
    );
  });

  return { promise, cancel: () => task.cancel() };
}

// ─── Audit zip validation (client-side, before upload) ────────────────────
//
// Lecture minimale du format ZIP (APPNOTE 4.3.16 / 4.4) sans dépendance :
// End Of Central Directory (EOCD) → central directory → noms des entrées.
// Seuls les en-têtes sont lus (au plus ~64 Ko en fin de fichier + le central
// directory) : aucune décompression, le fichier n'est jamais chargé en entier.

const SIG_EOCD = 0x06054b50;
const SIG_EOCD64_LOCATOR = 0x07064b50;
const SIG_EOCD64 = 0x06064b50;
const SIG_CENTRAL = 0x02014b50;
const MAX_COMMENT = 0xffff;
const EOCD_MIN = 22;
/** Taille max acceptée du central directory (≈ 100 000 entrées). */
const MAX_CENTRAL_DIR_BYTES = 16 * 1024 * 1024;

export const AUDIT_ZIP_MAX_BYTES = 500 * 1024 * 1024;

export type AuditZipErrorKey =
  | 'upload.zip.err.not_zip'
  | 'upload.zip.err.too_large'
  | 'upload.zip.err.too_small'
  | 'upload.zip.err.unreadable'
  | 'upload.zip.err.no_run'
  | 'upload.zip.err.recompressed'
  | 'upload.zip.err.nested'
  | 'upload.zip.err.multiple_runs'
  | 'upload.zip.err.manifest_mismatch';

export interface AuditZipValidation {
  ok: boolean;
  /** Clé i18n de l'erreur (`upload.zip.err.*`). */
  error?: AuditZipErrorKey;
  /** Dossier racine `run_YYYYMMDD_HHMMSS` détecté. */
  runDir?: string;
  /**
   * Empreinte SHA-256 du fichier, calculée dans le navigateur avant l'envoi
   * (ING-19 / SEC-08). Transmise au dépôt en métadonnée `sha256` ; le serveur
   * recalcule la sienne et signale toute divergence.
   */
  sha256?: string;
  /**
   * Nombre d'entrées du manifeste (`run_<id>/manifest.json`) confrontées au
   * central directory. `0` quand l'archive n'en porte pas — un zip antérieur
   * au manifeste reste parfaitement valide.
   */
  manifestChecked?: number;
  /** Membres du manifeste absents ou de taille différente (diagnostic). */
  manifestMismatched?: string[];
}

/** Une entrée du central directory, telle que le zip la déclare. */
export interface ZipEntryInfo {
  name: string;
  /** Méthode de compression : 0 = stocké, 8 = deflate. */
  method: number;
  compressedSize: number;
  uncompressedSize: number;
  localHeaderOffset: number;
}

const RUN_DIR = /^run_\d{8}_\d{6}$/;
const RUN_DIR_ANYWHERE = /(?:^|\/)(run_\d{8}_\d{6})\/run_metadata\.json$/;

async function readRange(file: Blob, start: number, end: number): Promise<DataView> {
  const buf = await file.slice(start, end).arrayBuffer();
  return new DataView(buf);
}

/** Localise l'EOCD dans les derniers 64 Ko + 22 octets du fichier. */
function findEocd(tail: DataView): number {
  for (let i = tail.byteLength - EOCD_MIN; i >= 0; i--) {
    if (tail.getUint32(i, true) === SIG_EOCD) return i;
  }
  return -1;
}

/** Champ « zip64 extended information » (0x0001) d'une entrée : tailles saturées. */
const ZIP64_EXTRA_ID = 0x0001;

function zip64Extra(cd: DataView, start: number, len: number, saturated: number[]): number[] {
  // Les valeurs 64 bits n'apparaissent que pour les champs saturés à 0xffffffff,
  // dans l'ordre : taille décompressée, taille compressée, offset local.
  const out = [...saturated];
  let pos = start;
  while (pos + 4 <= start + len) {
    const id = cd.getUint16(pos, true);
    const size = cd.getUint16(pos + 2, true);
    if (id === ZIP64_EXTRA_ID) {
      let at = pos + 4;
      for (let i = 0; i < out.length; i++) {
        if (out[i] === 0xffffffff && at + 8 <= pos + 4 + size) {
          out[i] = Number(cd.getBigUint64(at, true));
          at += 8;
        }
      }
      return out;
    }
    pos += 4 + size;
  }
  return out;
}

/**
 * Lit le central directory (zip32 et zip64) et rend une entrée par membre.
 * Lance une erreur si la structure est illisible. Seuls les en-têtes sont lus :
 * aucune décompression, le fichier n'est jamais chargé en entier.
 */
export async function listZipEntriesDetailed(file: Blob): Promise<ZipEntryInfo[]> {
  const tailStart = Math.max(0, file.size - (MAX_COMMENT + EOCD_MIN));
  const tail = await readRange(file, tailStart, file.size);
  const eocdRel = findEocd(tail);
  if (eocdRel < 0) throw new Error('EOCD not found');
  const eocdAbs = tailStart + eocdRel;

  let cdSize = tail.getUint32(eocdRel + 12, true);
  let cdOffset = tail.getUint32(eocdRel + 16, true);

  // zip64 : les champs 32 bits saturés renvoient vers l'enregistrement zip64.
  if (cdSize === 0xffffffff || cdOffset === 0xffffffff) {
    const locAbs = eocdAbs - 20;
    if (locAbs < 0) throw new Error('zip64 locator missing');
    const loc = await readRange(file, locAbs, locAbs + 20);
    if (loc.getUint32(0, true) !== SIG_EOCD64_LOCATOR) throw new Error('zip64 locator missing');
    const rec64Abs = Number(loc.getBigUint64(8, true));
    const rec = await readRange(file, rec64Abs, rec64Abs + 56);
    if (rec.getUint32(0, true) !== SIG_EOCD64) throw new Error('zip64 record missing');
    cdSize = Number(rec.getBigUint64(40, true));
    cdOffset = Number(rec.getBigUint64(48, true));
  }

  if (cdSize > MAX_CENTRAL_DIR_BYTES || cdOffset + cdSize > file.size) throw new Error('central directory out of bounds');
  const cd = await readRange(file, cdOffset, cdOffset + cdSize);
  const decoder = new TextDecoder('utf-8');
  const entries: ZipEntryInfo[] = [];
  let pos = 0;
  while (pos + 46 <= cd.byteLength && cd.getUint32(pos, true) === SIG_CENTRAL) {
    const nameLen = cd.getUint16(pos + 28, true);
    const extraLen = cd.getUint16(pos + 30, true);
    const commentLen = cd.getUint16(pos + 32, true);
    const [uncompressedSize, compressedSize, localHeaderOffset] = zip64Extra(
      cd, pos + 46 + nameLen, extraLen,
      [cd.getUint32(pos + 24, true), cd.getUint32(pos + 20, true), cd.getUint32(pos + 42, true)],
    );
    entries.push({
      name: decoder.decode(new Uint8Array(cd.buffer, cd.byteOffset + pos + 46, nameLen)),
      method: cd.getUint16(pos + 10, true),
      compressedSize,
      uncompressedSize,
      localHeaderOffset,
    });
    pos += 46 + nameLen + extraLen + commentLen;
  }
  if (entries.length === 0) throw new Error('empty central directory');
  return entries;
}

/** Noms d'entrées du central directory (compatibilité : `inspectAuditZipEntries`). */
export async function listZipEntries(file: Blob): Promise<string[]> {
  return (await listZipEntriesDetailed(file)).map(e => e.name);
}

// ─── Lecture d'un membre (manifeste) ──────────────────────────────────────

const LOCAL_HEADER_MIN = 30;
/** Un manifeste hors gabarit n'est pas lu : c'est un fichier de quelques Ko. */
const MAX_MANIFEST_BYTES = 4 * 1024 * 1024;

async function inflateRaw(data: Uint8Array): Promise<Uint8Array | null> {
  const Ctor = (globalThis as { DecompressionStream?: typeof DecompressionStream }).DecompressionStream;
  if (!Ctor) return null;   // navigateur sans DecompressionStream : on ne vérifie pas
  const stream = new Blob([data as BlobPart]).stream().pipeThrough(new Ctor('deflate-raw'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/**
 * Contenu d'un membre du zip, sans charger l'archive entière.
 * Rend `null` quand la méthode de compression n'est pas lisible ici — un
 * manifeste illisible n'est jamais une erreur de dépôt.
 */
export async function readZipEntry(file: Blob, entry: ZipEntryInfo): Promise<Uint8Array | null> {
  if (entry.uncompressedSize > MAX_MANIFEST_BYTES) return null;
  if (entry.method !== 0 && entry.method !== 8) return null;
  const header = await readRange(file, entry.localHeaderOffset, entry.localHeaderOffset + LOCAL_HEADER_MIN);
  const dataStart = entry.localHeaderOffset + LOCAL_HEADER_MIN
    + header.getUint16(26, true) + header.getUint16(28, true);
  const raw = new Uint8Array(
    await file.slice(dataStart, dataStart + entry.compressedSize).arrayBuffer());
  try {
    return entry.method === 0 ? raw : await inflateRaw(raw);
  } catch {
    return null;
  }
}

/** Une ligne du manifeste produit par le toolkit (`manifest.json`). */
export interface AuditZipManifestFile { path: string; size_bytes?: number; sha256?: string }
export interface AuditZipManifest { files: AuditZipManifestFile[] }

const MANIFEST_RE = /^(run_\d{8}_\d{6})\/manifest\.json$/;

/**
 * Lit le `manifest.json` du dossier `run_<id>` s'il existe. Rend `null` quand l'archive n'en
 * porte pas, ou quand il est illisible : un zip antérieur au manifeste reste
 * parfaitement valide.
 */
export async function readAuditZipManifest(
  file: Blob, entries: ZipEntryInfo[],
): Promise<{ runDir: string; manifest: AuditZipManifest } | null> {
  const entry = entries.find(e => MANIFEST_RE.test(e.name.replace(/\\/g, '/')));
  if (!entry) return null;
  const runDir = MANIFEST_RE.exec(entry.name.replace(/\\/g, '/'))![1];
  const bytes = await readZipEntry(file, entry);
  if (!bytes) return null;
  try {
    const parsed = JSON.parse(new TextDecoder('utf-8').decode(bytes)) as AuditZipManifest;
    return Array.isArray(parsed?.files) ? { runDir, manifest: parsed } : null;
  } catch {
    return null;
  }
}

/**
 * Confronte le manifeste au central directory : chaque fichier déclaré doit
 * exister dans l'archive avec la taille annoncée. C'est ce qui attrape un zip
 * tronqué ou recompressé AVANT 500 Mo de téléversement — sans décompresser
 * quoi que ce soit.
 */
export function verifyAuditZipManifest(
  manifest: AuditZipManifest, runDir: string, entries: ZipEntryInfo[],
): string[] {
  const sizes = new Map(entries.map(e => [e.name.replace(/\\/g, '/'), e.uncompressedSize] as const));
  const mismatched: string[] = [];
  for (const declared of manifest.files) {
    if (!declared || typeof declared.path !== 'string' || !declared.path) continue;
    const name = `${runDir}/${declared.path.replace(/\\/g, '/')}`;
    const actual = sizes.get(name);
    if (actual === undefined) mismatched.push(declared.path);
    else if (typeof declared.size_bytes === 'number' && declared.size_bytes !== actual) {
      mismatched.push(declared.path);
    }
  }
  return mismatched;
}

// ─── Empreinte SHA-256 (streaming, sans dépendance) ───────────────────────
//
// `crypto.subtle.digest` exige tout le fichier en mémoire : inutilisable sur
// un zip de plusieurs centaines de méga-octets. Cette implémentation avale le
// fichier tranche par tranche, ce qui borne la mémoire ET laisse la main au
// navigateur entre les tranches (l'interface ne gèle pas).

const K256 = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

const rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n));

class Sha256 {
  private h = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  private w = new Uint32Array(64);
  private buf = new Uint8Array(64);
  private bufLen = 0;
  private total = 0;

  update(chunk: Uint8Array): void {
    this.total += chunk.length;
    let i = 0;
    if (this.bufLen > 0) {
      const take = Math.min(64 - this.bufLen, chunk.length);
      this.buf.set(chunk.subarray(0, take), this.bufLen);
      this.bufLen += take;
      i = take;
      if (this.bufLen === 64) { this.block(this.buf, 0); this.bufLen = 0; }
    }
    for (; i + 64 <= chunk.length; i += 64) this.block(chunk, i);
    if (i < chunk.length) {
      this.buf.set(chunk.subarray(i), 0);
      this.bufLen = chunk.length - i;
    }
  }

  digest(): string {
    const bits = this.total * 8;
    const pad = new Uint8Array(this.bufLen < 56 ? 64 : 128);
    pad.set(this.buf.subarray(0, this.bufLen));
    pad[this.bufLen] = 0x80;
    const dv = new DataView(pad.buffer);
    dv.setUint32(pad.length - 8, Math.floor(bits / 2 ** 32), false);
    dv.setUint32(pad.length - 4, bits >>> 0, false);
    for (let i = 0; i < pad.length; i += 64) this.block(pad, i);
    let out = '';
    for (let i = 0; i < 8; i++) out += this.h[i].toString(16).padStart(8, '0');
    return out;
  }

  private block(data: Uint8Array, off: number): void {
    const w = this.w;
    for (let i = 0; i < 16; i++) {
      const p = off + i * 4;
      w[i] = (data[p] << 24) | (data[p + 1] << 16) | (data[p + 2] << 8) | data[p + 3];
    }
    for (let i = 16; i < 64; i++) {
      const a = w[i - 15], b = w[i - 2];
      const s0 = rotr(a, 7) ^ rotr(a, 18) ^ (a >>> 3);
      const s1 = rotr(b, 17) ^ rotr(b, 19) ^ (b >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) | 0;
    }
    let a = this.h[0], b = this.h[1], c = this.h[2], d = this.h[3];
    let e = this.h[4], f = this.h[5], g = this.h[6], h = this.h[7];
    for (let i = 0; i < 64; i++) {
      const t1 = (h + (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) + ((e & f) ^ (~e & g)) + K256[i] + w[i]) | 0;
      const t2 = ((rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) | 0;
      h = g; g = f; f = e; e = (d + t1) | 0;
      d = c; c = b; b = a; a = (t1 + t2) | 0;
    }
    this.h[0] += a; this.h[1] += b; this.h[2] += c; this.h[3] += d;
    this.h[4] += e; this.h[5] += f; this.h[6] += g; this.h[7] += h;
  }
}

const SHA256_CHUNK_BYTES = 4 * 1024 * 1024;

/**
 * Empreinte SHA-256 d'un fichier, en hexadécimal minuscule.
 *
 * `chunkBytes` borne la mémoire : le fichier n'est jamais chargé en entier.
 * Il est réglable pour les tests (tranches non alignées sur 64 octets).
 */
export async function sha256Hex(
  blob: Blob,
  onProgress?: (done: number, total: number) => void,
  chunkBytes: number = SHA256_CHUNK_BYTES,
): Promise<string> {
  const hash = new Sha256();
  for (let start = 0; start < blob.size; start += chunkBytes) {
    const end = Math.min(start + chunkBytes, blob.size);
    hash.update(new Uint8Array(await blob.slice(start, end).arrayBuffer()));
    onProgress?.(end, blob.size);
  }
  return hash.digest();
}

/**
 * Analyse une liste d'entrées et vérifie le layout du contrat :
 * `run_YYYYMMDD_HHMMSS/run_metadata.json` à la racine, un seul run, pas de
 * dossier parent ajouté par une recompression, pas de zip imbriqué.
 */
export function inspectAuditZipEntries(names: string[]): AuditZipValidation {
  const normalized = names.map(n => n.replace(/\\/g, '/'));
  const roots = new Set<string>();
  const nestedRuns = new Set<string>();
  for (const n of normalized) {
    const m = RUN_DIR_ANYWHERE.exec(n);
    if (!m) continue;
    if (n === `${m[1]}/run_metadata.json`) roots.add(m[1]);
    else nestedRuns.add(m[1]);
  }
  if (roots.size === 1) return { ok: true, runDir: [...roots][0] };
  if (roots.size > 1) return { ok: false, error: 'upload.zip.err.multiple_runs' };
  if (nestedRuns.size > 0) return { ok: false, error: 'upload.zip.err.recompressed' };
  if (normalized.some(n => /\.zip$/i.test(n) && RUN_DIR.test(n.split('/').pop()!.replace(/\.zip$/i, '')))) {
    return { ok: false, error: 'upload.zip.err.nested' };
  }
  return { ok: false, error: 'upload.zip.err.no_run' };
}

/**
 * Valide un zip d'audit avant l'upload : extension, taille, structure (lecture
 * du central directory côté navigateur), puis **intégrité** (ING-19 / SEC-08) :
 *
 * 1. si l'archive porte un `run_<id>/manifest.json`, chaque fichier déclaré doit
 *    exister avec la taille annoncée — un zip tronqué ou recompressé est
 *    refusé AVANT 500 Mo de téléversement ;
 * 2. l'empreinte SHA-256 du fichier est calculée et renvoyée : elle part en
 *    métadonnée du dépôt, le serveur recalcule la sienne et signale une
 *    divergence sur l'AuditDoc.
 *
 * `onHashProgress` permet d'afficher l'avancement du calcul sur un gros zip.
 * L'erreur est une clé i18n.
 */
export async function validateAuditZip(
  file: File, onHashProgress?: (done: number, total: number) => void,
): Promise<AuditZipValidation> {
  if (!/\.zip$/i.test(file.name)) return { ok: false, error: 'upload.zip.err.not_zip' };
  if (file.size > AUDIT_ZIP_MAX_BYTES) return { ok: false, error: 'upload.zip.err.too_large' };
  if (file.size < 1024) return { ok: false, error: 'upload.zip.err.too_small' };
  let entries: ZipEntryInfo[];
  try {
    entries = await listZipEntriesDetailed(file);
  } catch {
    return { ok: false, error: 'upload.zip.err.unreadable' };
  }
  const layout = inspectAuditZipEntries(entries.map(e => e.name));
  if (!layout.ok) return layout;

  let manifestChecked = 0;
  const found = await readAuditZipManifest(file, entries);
  if (found) {
    const mismatched = verifyAuditZipManifest(found.manifest, found.runDir, entries);
    if (mismatched.length > 0) {
      return { ok: false, error: 'upload.zip.err.manifest_mismatch',
               runDir: layout.runDir, manifestMismatched: mismatched };
    }
    manifestChecked = found.manifest.files.length;
  }
  return { ...layout, manifestChecked, sha256: await sha256Hex(file, onHashProgress) };
}
